import { useEffect, useMemo, useRef, useState } from 'react'
import { CustomInput } from 'src/consumer/components/helperComponents'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { DosagePillItem } from '../drugAdvice/components/styledComponents'
import PrescriptionPillOptionsInputComponent from './PrescriptionPillOptionsInputComponent'

export default function PillInputComponent({
  value,
  handleChange,
  options,
  getOptionValue,
  getOptionLabel,
  valueKey,
  pillUnselectedBackground,
  // Opt-in (used by the drug-advice contextual drawer): renders a search box above the pills,
  // filters the options as you type, lets you commit a typed custom value, and caps the pill
  // area to `maxPillRows` rows (the rest stay reachable via the search box). Defaults keep the
  // original behaviour for the other consumers (DosageComponent, ObservationsContextForm).
  searchable = false,
  searchPlaceholder = 'Search or type a custom value…',
  maxPillRows,
}) {
  const otherOptionNotSelected = options.map((dos) => getOptionValue(dos))?.includes(value)
  const customInputRef = useRef()
  const [searchQuery, setSearchQuery] = useState('')

  // Local state for the "Other" free-text input — immediate display, debounced parent update
  const isOtherValue = !otherOptionNotSelected
  const [otherLocal, setOtherLocal] = useState(isOtherValue ? (value || '') : '')
  const _otherTimerRef = useRef(null)
  const _handleChangeRef = useRef(handleChange)
  _handleChangeRef.current = handleChange

  // Sync local state when canonical value changes (e.g., form opened with existing "other" text)
  useEffect(() => {
    if (!options.map((dos) => getOptionValue(dos))?.includes(value)) {
      setOtherLocal(value || '')
    } else {
      setOtherLocal('')
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(_otherTimerRef.current), [])

  function commitValue(newVal) {
    clearTimeout(_otherTimerRef.current)
    setOtherLocal('')
    handleChange({ target: { value: newVal, id: valueKey } })
  }

  const trimmedQuery = (searchQuery || '').trim()
  const filteredOptions = useMemo(() => {
    if (!searchable || !trimmedQuery) return options || []
    const q = trimmedQuery.toLowerCase()
    return (options || []).filter((o) =>
      String(getOptionLabel(o) ?? '')
        .toLowerCase()
        .includes(q),
    )
  }, [options, trimmedQuery, searchable, getOptionLabel])

  if (searchable) {
    const hasCustomValue = !!value && !otherOptionNotSelected
    const queryMatchesOption = (options || []).some(
      (o) => String(getOptionLabel(o) ?? '').toLowerCase() === trimmedQuery.toLowerCase(),
    )
    // Cap the quick-pick pills to a couple of rows; everything else stays reachable via search.
    const pillsAreaStyle = maxPillRows
      ? { maxHeight: `${maxPillRows * 42}px`, overflowY: 'auto' }
      : {}
    return (
      <div className="d-flex flex-column gap-2">
        <CustomInput
          inputRef={customInputRef}
          value={searchQuery}
          placeholder={searchPlaceholder}
          onChange={(e) => setSearchQuery(e.target.value)}
          inputAnsMessageContainerStyles={{ padding: '8px 14px' }}
          showClearInputButton
        />
        <div className="d-flex flex-wrap gap-1" style={pillsAreaStyle}>
          {hasCustomValue && (
            <DosagePillItem selected onClick={() => commitValue('')}>
              {value}&nbsp;✕
            </DosagePillItem>
          )}
          {trimmedQuery && !queryMatchesOption && (
            <DosagePillItem
              onClick={() => {
                commitValue(trimmedQuery)
                setSearchQuery('')
              }}
            >
              Use “{trimmedQuery}”
            </DosagePillItem>
          )}
          {filteredOptions.map((option, idx) => (
            <DosagePillItem
              key={idx}
              pillUnselectedBackground={pillUnselectedBackground}
              selected={value === getOptionValue(option)}
              onClick={() => commitValue(getOptionValue(option))}
            >
              {getOptionLabel(option)}
            </DosagePillItem>
          ))}
        </div>
      </div>
    )
  }

  async function handleOtherPillClick() {
    clearTimeout(_otherTimerRef.current)
    setOtherLocal('')
    handleChange({ target: { value: '', id: valueKey } })
    await sleep(300)
    customInputRef.current.focus()
  }

  return (
    <div>
      <PrescriptionPillOptionsInputComponent
        options={options}
        onChange={(newValue) => {
          // Predefined pill selected — cancel any pending "Other" text debounce immediately
          clearTimeout(_otherTimerRef.current)
          setOtherLocal('')
          handleChange({ target: { value: getOptionValue(newValue), id: valueKey } })
        }}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        value={value}
        pillUnselectedBackground={pillUnselectedBackground}
      ></PrescriptionPillOptionsInputComponent>
      <div className="d-flex align-items-center gap-1 mt-1">
        <DosagePillItem selected={!otherOptionNotSelected} onClick={handleOtherPillClick}>
          Other
        </DosagePillItem>
        {!otherOptionNotSelected && (
          <div style={{ maxWidth: '100%' }}>
            <CustomInput
              inputRef={customInputRef}
              value={otherLocal}
              onChange={(e) => {
                const val = e.target.value
                setOtherLocal(val)
                clearTimeout(_otherTimerRef.current)
                _otherTimerRef.current = setTimeout(() => {
                  _handleChangeRef.current({ target: { value: val, id: valueKey } })
                }, 300)
              }}
              id={valueKey}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// inputAnsMessageContainerStyles={{
//   background: '#ebebeb',
// }}
// pureInputProps={{
//   style: {
//     color:
//       options.map((dos) => getOptionValue(dos))?.includes(value) || value === ''
//         ? '#000'
//         : '#fff',
//   },
// }}
